import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { CustomAutocomplete, CustomAutocompleteProps } from 'web-library';

export interface ControlledAutocompleteTransformProp {
  input: (value: any, options: any[]) => any | null;
  output: (newValue: any | null) => any;
}

interface ControlledAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends CustomAutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  control: Control<any>;
  name: string;
  rules?: Object;
  /**
   * Use this prop to transform value of selected dropdown item.
   *
   * This is particularly useful when this component is used together with react-hook-form and the
   * dropdown list consists of an array of objects. This transform prop could be used to extract the value of selected
   * object.
   *
   * Reference and example: https://react-hook-form.com/advanced-usage#TransformandParse
   */
  transform?: ControlledAutocompleteTransformProp;
}

const ControlledAutocomplete = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>({
  className,
  control,
  defaultValue,
  disabled,
  getOptionLabel,
  isOptionEqualToValue,
  label,
  loading,
  loadingError,
  loadingErrorOnReFetch,
  name,
  options,
  placeholder,
  error,
  helperText,
  rules = undefined,
  transform = undefined,
  ...otherProps
}: ControlledAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>) => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    // It is encouraged that you set a defaultValue for all inputs to non-undefined such as the empty string or null.
    // reference: https://react-hook-form.com/api/#Controller
    defaultValue={defaultValue}
    render={({ field }) => (
      <CustomAutocomplete
        className={className}
        disabled={disabled}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        label={label}
        loading={loading}
        loadingError={loadingError}
        loadingErrorOnReFetch={loadingErrorOnReFetch}
        onChange={(e, newValue) => field.onChange(transform ? transform.output(newValue) : newValue)}
        options={options}
        placeholder={placeholder}
        value={transform ? transform.input(field.value, options) : field.value}
        error={error}
        helperText={helperText}
        {...otherProps}
      />
    )}
  />
);

export default ControlledAutocomplete;
